/**
 * Layer activation policy (ADR-017 / `LAYER_COMPOSITION_AND_ACTIVATION.md` §2).
 *
 * Resolves the activation **tier** for every renderable layer:
 *
 *   - `default`   — mount + show on project open (Google-Maps-grade first paint).
 *   - `deferred`  — register in tree + layer panel; engine mount waits for a trigger
 *                   (viewport intersection, zoom range, preset switch, globe mode).
 *   - `on-demand` — register only; user must toggle to mount + show.
 *
 * Resolution order (first match wins):
 *
 *   1. Per-item override on the layer (`activationTier`) — typically populated
 *      from `SceneItem.activation` written by the converter or operator.
 *   2. Size threshold (caller-supplied via {@link ActivationContext.sizeDemotion}).
 *      Anything that exceeds the per-kind budget gets demoted one tier
 *      (`default → deferred`). The handoff `budgets.ts` already enforces hard
 *      caps, this is the soft cap that hides oversized things behind a trigger.
 *   3. PLATEAU street-detail rule — historically `listPlateauDeferredDetailLayers`
 *      in TwinShell. The rule lives here now; that helper stays as a thin
 *      adapter that re-uses these matchers.
 *   4. Slot + kind policy default — the §2.2 table in
 *      `LAYER_COMPOSITION_AND_ACTIVATION.md`.
 *
 * The policy is intentionally a pure module — no React, no I/O — so it can be
 * unit-tested without a Vite environment and consumed by any caller that holds
 * a `Layer` (intake, scene-item mapping, scene-tree wiring, gate tests).
 */

import type { Layer, LayerKind } from '../types';
import {
  isPlateauDeferrableDetailLayer,
  isPlateauBasemapLayer,
  isPlateauPrimaryLayer,
  PLATEAU_STREET_DETAIL_DISTANCE_M,
  PLATEAU_STREET_DETAIL_RELEASE_DISTANCE_M,
} from '@/engines/giro3d/plateauMountPolicy';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type ActivationTier = 'default' | 'deferred' | 'on-demand';

export type ActivationTrigger =
  | 'viewport-and-zoom'
  | 'zoom'
  | 'globe-mode'
  | 'preset-switch'
  | 'user-toggle'
  | 'tree-selection'
  | 'panel-opened'
  | 'none';

export interface ActivationDecision {
  tier: ActivationTier;
  trigger: ActivationTrigger;
  /** Free-form reason, useful for tooltips, logs, and tests. */
  reason: string;
}

export interface ActivationPolicy {
  version: 1;
  defaults: Record<LayerKind, ActivationDecision>;
  /** Discriminated overrides applied before the kind default. */
  overrides: ActivationOverride[];
}

export interface ActivationOverride {
  when: {
    layerKind?: LayerKind;
    /** Match a `style.gtwinSlot` value (see `LayerKind`'s table in §2.2). */
    slot?: string;
    /** Match arbitrary `style.<key> === value` pairs. */
    style?: Record<string, string | boolean | number>;
  };
  decision: ActivationDecision;
}

export interface ActivationContext {
  /** Optional handoff size demotion — set this when the per-kind budget is breached. */
  sizeDemotion?: boolean;
  /**
   * Optional inline override on a specific layer (typically `SceneItem.activation`
   * plumbed into the layer at intake). Wins over both size demotion and the
   * policy default.
   */
  itemOverride?: ActivationTier;
}

// ---------------------------------------------------------------------------
// Default policy (§2.2 table)
// ---------------------------------------------------------------------------

const D = (
  tier: ActivationTier,
  trigger: ActivationTrigger,
  reason: string,
): ActivationDecision => ({ tier, trigger, reason });

export const DEFAULT_ACTIVATION_POLICY: ActivationPolicy = {
  version: 1,
  defaults: {
    raster: D('default', 'none', 'basemap / raster tiles render immediately'),
    terrain: D('default', 'none', 'COG ColorLayer terrain renders immediately'),
    vector: D('default', 'none', 'vector overlay loads with first paint'),
    '3dtiles': D('deferred', 'viewport-and-zoom', '3D Tiles deferred until viewport ∩ bbox + zoom threshold'),
    bim: D('deferred', 'viewport-and-zoom', 'BIM models deferred until viewport ∩ bbox'),
    pointcloud: D('on-demand', 'user-toggle', 'Point clouds load only on explicit user action'),
    timeseries: D('on-demand', 'panel-opened', 'Sensor/timeseries load when panel opens'),
    sensor: D('on-demand', 'panel-opened', 'Sensor stream loads when panel opens'),
    panorama: D('on-demand', 'user-toggle', 'Panorama pose registered, opens on toggle'),
    'panorama-bubble': D('on-demand', 'tree-selection', 'Panorama bubble enters on station/tour selection'),
    // Sky/sun/fog default-on at the controller level — the controller decides
    // per sub-layer. The Layer level here represents the synthetic catalog
    // entry; tag it default so it appears in the tree but the controller
    // owns per-effect tier.
    environment: D('default', 'none', 'Environment controller handles per-effect mount'),
  },
  overrides: [
    // Terrain quantized-mesh -> Cesium globe: deferred until globe mode entered.
    {
      when: { layerKind: 'terrain', style: { intakeKind: 'quantized-mesh' } },
      decision: D('deferred', 'globe-mode', 'Quantized-mesh terrain mounts in globe mode'),
    },
    // DEM-contour vector overlays: deferred until zoom threshold (the converter
    // tags them with intakeKind='dem-contours' via artifactToLayer).
    {
      when: { layerKind: 'vector', style: { intakeKind: 'dem-contours' } },
      decision: D('deferred', 'zoom', 'DEM contour vectors arrive at zoom threshold'),
    },
  ],
};

// ---------------------------------------------------------------------------
// Resolution
// ---------------------------------------------------------------------------

/**
 * Resolve the activation decision for one layer.
 *
 * Order: itemOverride → PLATEAU street-detail → sizeDemotion → policy.overrides
 * → policy.defaults[kind].
 */
export function resolveActivation(
  layer: Layer,
  ctx: ActivationContext = {},
  policy: ActivationPolicy = DEFAULT_ACTIVATION_POLICY,
): ActivationDecision {
  // 1. Per-item override wins.
  if (ctx.itemOverride) {
    return D(ctx.itemOverride, 'none', `SceneItem.activation override = ${ctx.itemOverride}`);
  }

  // 2. PLATEAU street-detail rule — preserved verbatim from the old TwinShell
  //    helper so behaviour is identical for PLATEAU Tokyo scenes. Basemap /
  //    primary layers are excluded by `isPlateauDeferrableDetailLayer` itself.
  if (
    !isPlateauBasemapLayer({ id: layer.id, kind: layer.kind, style: layer.style })
    && !isPlateauPrimaryLayer(layer.id, layer.label)
    && isPlateauDeferrableDetailLayer(layer.id, layer.label, layer.style, layer.kind)
  ) {
    return D(
      'deferred',
      'viewport-and-zoom',
      'PLATEAU street-detail layer — deferred behind street-level zoom gate',
    );
  }

  // 3. Size demotion — only demotes a 'default' decision to 'deferred'.
  const baseDecision = matchOverride(layer, policy) ?? policy.defaults[layer.kind];
  if (!baseDecision) {
    return D('default', 'none', `no policy entry for kind=${layer.kind} (fallback to default)`);
  }
  if (ctx.sizeDemotion && baseDecision.tier === 'default') {
    return D(
      'deferred',
      'viewport-and-zoom',
      `${baseDecision.reason} — demoted by size budget`,
    );
  }
  return baseDecision;
}

function matchOverride(layer: Layer, policy: ActivationPolicy): ActivationDecision | null {
  const slot = readStyleString(layer, 'gtwinSlot');
  for (const override of policy.overrides) {
    if (override.when.layerKind && override.when.layerKind !== layer.kind) continue;
    if (override.when.slot && override.when.slot !== slot) continue;
    if (override.when.style) {
      let allMatch = true;
      for (const [key, expected] of Object.entries(override.when.style)) {
        if (layer.style?.[key] !== expected) {
          allMatch = false;
          break;
        }
      }
      if (!allMatch) continue;
    }
    return override.decision;
  }
  return null;
}

function readStyleString(layer: Layer, key: string): string | undefined {
  const value = layer.style?.[key];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

// ---------------------------------------------------------------------------
// Helpers for catalog-level decisions
// ---------------------------------------------------------------------------

/**
 * Initial-visibility rule for `setCatalog`. Layers in tier `default` or
 * `deferred` arrive visible (the engine mount layer decides when to actually
 * load); `on-demand` layers arrive registered but hidden until the user toggles
 * them.
 *
 * NOTE: explicit `visible: boolean` values on the gTwin envelope still win —
 * callers should apply this rule first, then overlay per-item visibility
 * flags via `setVisible`.
 */
export function initialVisibilityForTier(tier: ActivationTier): boolean {
  return tier !== 'on-demand';
}

/**
 * Subset of the catalog that the deferred-mount controller should consider
 * holding back until its trigger fires. Layers that already mounted are
 * filtered out. Replaces the old `listPlateauDeferredDetailLayers` helper —
 * the PLATEAU rule is now one branch of `resolveActivation`.
 */
export function listDeferredCandidates<
  T extends { id: string; label?: string; kind?: string; style?: unknown },
>(
  visibleLayers: readonly T[],
  mountedLayers: readonly T[],
): T[] {
  const mountedIds = new Set(mountedLayers.map((layer) => layer.id));
  return visibleLayers.filter((layer) => {
    if (mountedIds.has(layer.id)) return false;
    if (!layer.kind) return false;
    // We don't have full Layer typing in the SceneTreeV2/TwinShell path, but
    // the only test that matters today is "would the PLATEAU rule defer it?"
    // — which is the only deferral the unified-Giro3D adapter knows how to
    // wait for. Other deferred tiers (quantized-mesh in globe mode, DEM
    // contours by zoom) are owned by other controllers.
    return isPlateauDeferrableDetailLayer(layer.id, layer.label, layer.style, layer.kind);
  });
}

// ---------------------------------------------------------------------------
// Generalised deferred trigger (Phase 2 — RFC-108-G / interop §4)
//
// Phase 1 left the deferred-mount controller wired through
// `listDeferredCandidates`, which only knows the PLATEAU street-detail
// distance gate (a single global threshold). The boss directive for Phase 2
// is "deferred-tier triggers generalised from the PLATEAU pattern: viewport
// ∩ bbox + zoom". The generalised contract:
//
//   - Each deferred layer carries its own trigger config (zoom distance
//     thresholds, optional bbox-intersection requirement).
//   - PLATEAU street detail is one config preset (1000 m zoom-in, 1500 m
//     release hysteresis; bbox-intersection skipped — the global gate
//     suffices because all PLATEAU tilesets cover the same Tokyo footprint).
//   - Non-PLATEAU 3D Tiles + BIM use viewport ∩ bbox + zoom-by-default —
//     mount when the camera footprint intersects the layer bbox AND the
//     camera distance is below the per-kind threshold.
//   - The trigger is evaluated per-layer; the controller in TwinShell loops
//     deferred layers and asks `evaluateDeferredTrigger` for each.
//
// The contract intentionally does NOT include scene-graph traversal: it
// receives a `MountTriggerContext` snapshot (camera distance, viewport
// footprint bbox) so it stays a pure function. The controller computes the
// snapshot once per camera tick and reuses it.
// ---------------------------------------------------------------------------

/** Bounding-box shape used by trigger evaluation. */
export type LngLatBbox = readonly [number, number, number, number];

/** Per-trigger configuration on a `deferred` decision. */
export interface DeferredTriggerConfig {
  /** Camera-to-layer distance at which the trigger fires (mount allowed). */
  zoomInDistanceM?: number;
  /**
   * Camera-to-layer distance at which the trigger releases (allow another
   * mount-cycle after a zoom-out). Implements a Schmitt-trigger hysteresis
   * so we don't oscillate around `zoomInDistanceM`.
   */
  releaseDistanceM?: number;
  /**
   * When `true`, require the camera footprint bbox to intersect the layer
   * bbox before the trigger fires. PLATEAU global gate sets this false.
   */
  requireViewportIntersection?: boolean;
}

/**
 * Snapshot of the host state used to evaluate triggers. Captured once per
 * camera tick by the deferred-mount controller; passed unchanged to every
 * `evaluateDeferredTrigger` call.
 */
export interface MountTriggerContext {
  /**
   * Distance from the camera to the project / scene origin in metres.
   * The PLATEAU controller has been using this since Phase 0 and it is
   * sufficient for "is the camera close enough" checks — the layer bbox
   * intersection test is separate.
   */
  cameraDistanceM: number | null;
  /**
   * Approximate longitude/latitude footprint of the current viewport
   * (4326). Optional — when absent, the bbox-intersection check is
   * skipped and the trigger reduces to a pure zoom gate.
   */
  viewportFootprint?: LngLatBbox;
  /**
   * Per-layer mount latch: layers whose trigger has fired in a previous
   * tick stay mounted until the user toggles them off — release distance
   * is for new mounts only, not for unmounting.
   */
  previouslyTriggered?: ReadonlySet<string>;
}

/**
 * Per-`LayerKind` default trigger config. The PLATEAU street-detail
 * distance set in `plateauMountPolicy.ts` becomes the default for 3D Tiles
 * and BIM; quantized-mesh terrain and dem-contours have their own.
 */
const DEFAULT_TRIGGER_BY_KIND: Partial<Record<LayerKind, DeferredTriggerConfig>> = {
  '3dtiles': {
    zoomInDistanceM: PLATEAU_STREET_DETAIL_DISTANCE_M,
    releaseDistanceM: PLATEAU_STREET_DETAIL_RELEASE_DISTANCE_M,
    requireViewportIntersection: true,
  },
  bim: {
    zoomInDistanceM: PLATEAU_STREET_DETAIL_DISTANCE_M,
    releaseDistanceM: PLATEAU_STREET_DETAIL_RELEASE_DISTANCE_M,
    requireViewportIntersection: true,
  },
  vector: {
    // Used only by dem-contours-style deferrals; default to a generous
    // distance until the converter expresses an interval threshold.
    zoomInDistanceM: 3_000,
    releaseDistanceM: 4_500,
    requireViewportIntersection: false,
  },
  terrain: {
    // quantized-mesh deferral is owned by the globe-mode switch; the
    // distance trigger is a fallback when no controller is wired.
    zoomInDistanceM: Number.POSITIVE_INFINITY,
    releaseDistanceM: Number.POSITIVE_INFINITY,
    requireViewportIntersection: false,
  },
};

/**
 * Per-layer trigger override resolution: `layer.style.deferredTrigger`
 * (typed via a `DeferredTriggerConfig` shape on the SceneItem) wins over
 * the kind default. Returns `null` for layers whose tier is not `deferred`
 * (the trigger never fires — caller should not be asking).
 */
export function triggerConfigFor(
  layer: { kind?: string; style?: unknown; activationTier?: string | null },
): DeferredTriggerConfig | null {
  if (layer.activationTier && layer.activationTier !== 'deferred') return null;
  const override = readStyleObject(layer, 'deferredTrigger') as Partial<DeferredTriggerConfig> | undefined;
  const base = layer.kind ? DEFAULT_TRIGGER_BY_KIND[layer.kind as LayerKind] : undefined;
  return { ...(base ?? {}), ...(override ?? {}) };
}

function readStyleObject(
  layer: { style?: unknown },
  key: string,
): Record<string, unknown> | undefined {
  if (!layer.style || typeof layer.style !== 'object') return undefined;
  const value = (layer.style as Record<string, unknown>)[key];
  if (value && typeof value === 'object') return value as Record<string, unknown>;
  return undefined;
}

function bboxesIntersect(a: LngLatBbox | undefined, b: LngLatBbox | undefined): boolean {
  if (!a || !b) return true; // unknown footprint → don't block
  if (!Number.isFinite(a[0]) || !Number.isFinite(b[0])) return true;
  const [aMinX, aMinY, aMaxX, aMaxY] = a;
  const [bMinX, bMinY, bMaxX, bMaxY] = b;
  if (aMaxX < bMinX || bMaxX < aMinX) return false;
  if (aMaxY < bMinY || bMaxY < aMinY) return false;
  return true;
}

export interface TriggerEvaluation {
  /** When `true`, the layer should mount now. */
  ready: boolean;
  /** Free-form reason — used by chips/tooltips and logged in tests. */
  reason: string;
}

/**
 * Decide whether a single deferred layer's trigger has fired in the
 * current camera/viewport snapshot.
 *
 *  - PLATEAU global gate: distance-only, evaluated against
 *    `cameraDistanceM`. The legacy controller still drives this for the
 *    PLATEAU group as a whole; per-layer evaluation matches identically
 *    because all PLATEAU detail layers share the same threshold.
 *  - Non-PLATEAU 3D Tiles / BIM: zoom + viewport ∩ bbox.
 *  - Globe-mode terrain / DEM contours: distance only by default;
 *    callers can override via `layer.style.deferredTrigger`.
 */
export function evaluateDeferredTrigger<
  T extends {
    id: string;
    kind?: string;
    style?: unknown;
    activationTier?: string | null;
    bbox?: LngLatBbox | readonly number[];
  },
>(layer: T, ctx: MountTriggerContext): TriggerEvaluation {
  const config = triggerConfigFor(layer);
  if (!config) {
    return { ready: true, reason: 'tier is not deferred — no trigger to evaluate' };
  }

  // Phase-2 sticky-mount: once triggered, a layer stays "ready" until the
  // user toggles it off. The release distance only matters for layers that
  // have NOT yet mounted.
  if (ctx.previouslyTriggered?.has(layer.id)) {
    return { ready: true, reason: 'previously triggered — sticky mount' };
  }

  const zoomInDistance = config.zoomInDistanceM;
  const distance = ctx.cameraDistanceM;
  if (typeof zoomInDistance === 'number' && Number.isFinite(zoomInDistance)) {
    if (distance == null || !Number.isFinite(distance)) {
      return {
        ready: false,
        reason: 'camera distance unknown — waiting for first camera tick',
      };
    }
    if (distance > zoomInDistance) {
      return {
        ready: false,
        reason: `camera ${Math.round(distance)} m above zoom-in threshold ${zoomInDistance} m`,
      };
    }
  }

  if (config.requireViewportIntersection) {
    const layerBbox = layer.bbox && layer.bbox.length >= 4
      ? ([layer.bbox[0], layer.bbox[1], layer.bbox[2], layer.bbox[3]] as LngLatBbox)
      : undefined;
    if (!bboxesIntersect(ctx.viewportFootprint, layerBbox)) {
      return {
        ready: false,
        reason: 'layer bbox outside viewport footprint',
      };
    }
  }

  return { ready: true, reason: 'trigger fired (zoom + viewport)' };
}

/**
 * The mounted-set-aware version of `evaluateDeferredTrigger` used by the
 * deferred-mount controller. Returns the subset of `visibleLayers` whose
 * trigger has fired AND that are not yet in `mountedLayers` — the engine
 * adapter should mount these next tick.
 */
// ---------------------------------------------------------------------------
// On-demand tier (Phase 3 — point clouds / panorama)
//
// `on-demand` layers register in the tree but do not engine-mount until the
// operator toggles visibility ON. TwinShell's mount loop consults
// `evaluateOnDemandTrigger` so basemap/environment sticky-mount exceptions do
// not accidentally pull a point cloud into the engine on project open.
// ---------------------------------------------------------------------------

export interface OnDemandTriggerContext {
  /** Whether the layer is in the operator's visible set (eye toggle / preset). */
  visible: boolean;
}

export interface OnDemandEvaluation {
  /** When `true`, the layer may enter the engine mount set. */
  shouldMount: boolean;
  reason: string;
}

/**
 * Decide whether an `on-demand` layer should mount in the current tick.
 * Non-on-demand layers always return `shouldMount: true` so callers can use
 * this as a uniform gate ahead of `userVisibleLayers` filtering.
 */
export function evaluateOnDemandTrigger<
  T extends { id: string; activationTier?: string | null },
>(layer: T, ctx: OnDemandTriggerContext): OnDemandEvaluation {
  if (layer.activationTier !== 'on-demand') {
    return { shouldMount: true, reason: 'tier is not on-demand' };
  }
  if (ctx.visible) {
    return { shouldMount: true, reason: 'operator toggled on-demand layer on' };
  }
  return {
    shouldMount: false,
    reason: 'on-demand layer registered — awaiting user toggle',
  };
}

export function listReadyToMountDeferred<
  T extends {
    id: string;
    kind?: string;
    style?: unknown;
    label?: string;
    activationTier?: string | null;
    bbox?: LngLatBbox | readonly number[];
  },
>(
  visibleLayers: readonly T[],
  mountedLayers: readonly T[],
  ctx: MountTriggerContext,
): T[] {
  const mountedIds = new Set(mountedLayers.map((layer) => layer.id));
  return visibleLayers.filter((layer) => {
    if (mountedIds.has(layer.id)) return false;
    if (layer.activationTier && layer.activationTier !== 'deferred') return false;
    const { ready } = evaluateDeferredTrigger(layer, ctx);
    return ready;
  });
}
